import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Stack,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  useMediaQuery,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  CloudUpload as UploadIcon,
  GetApp as DownloadIcon,
  CheckCircle,
  Warning,
  Link as LinkIcon,
} from '@mui/icons-material';
import { Device } from '../../types';
import { vehicleAPI, deviceAPI } from '../../services/api';
import AddDeviceDialog from '../../components/AddDeviceDialog';
import BulkImportDialog from '../../components/BulkImportDialog';


const isPopulatedVehicle = (vehicleId: any): vehicleId is {
  _id: string;
  plateNumber: string;
  make: string;
  model: string;
  year: number;
} => {
  return vehicleId && typeof vehicleId === 'object' && 'plateNumber' in vehicleId;
};



interface DeviceWithTelemetry extends Device {
  lastSpeed?: number;
  latitude?: number;
  longitude?: number;
  address?: string;
  engineStatus?: boolean;
  lastUpdate?: string;
  heading?: number;
  altitude?: number;
  satellites?: number;
  batteryVoltage?: string | null; // Allow null
  externalVoltage?: string | null; // Allow null
  totalDistance?: number;
  gsmSignal?: number;
  idleTime?: number;
}

// Helper functions
const getCardinalDirection = (degrees: number): string => {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(degrees / 45) % 8;
  return directions[index];
};

const renderSignalBars = (signal: number): string => {
  const bars = ['▂', '▄', '▆', '█', '█'];
  if (signal === 0) return '✗';
  return bars.slice(0, Math.min(signal, 5)).join('');
};



const getDeviceRealTimeStatus = (device: DeviceWithTelemetry): {
  status: string;
  label: string;
  color: 'error' | 'success' | 'default' | 'info' | 'warning' | 'primary' | 'secondary';
} => {
  // Use device.status from MongoDB as primary source
  // (set by broadcastTelemetryUpdate + scheduler)
  if (device.status === 'inactive') {
    return { status: 'inactive', label: 'Inactive', color: 'default' };
  }
  if (device.status === 'online') {
    return { status: 'online', label: 'Online', color: 'success' };
  }
  if (device.status === 'offline') {
    return { status: 'offline', label: 'Offline', color: 'error' };
  }

  // Fallback to lastUpdate from telemetry if status not set
  const lastUpdateTime = device.lastUpdate
    ? new Date(device.lastUpdate).getTime() : 0;
  const minutesSinceUpdate = (Date.now() - lastUpdateTime) / 60000;
  if (!device.lastUpdate || minutesSinceUpdate > 15) {
    return { status: 'offline', label: 'Offline', color: 'error' };
  }
  if ((device.lastSpeed && device.lastSpeed > 0) || device.engineStatus) {
    return { status: 'online', label: 'Online', color: 'success' };
  }
  return { status: 'offline', label: 'Offline', color: 'error' };
};

// Location Loader Component
const LocationCell: React.FC<{
  latitude?: number;
  longitude?: number;
  initialAddress?: string;
}> = ({ latitude, longitude, initialAddress }) => {
  const [address, setAddress] = useState(initialAddress || 'Click to load');
  const [loading, setLoading] = useState(false);

  const loadAddress = async () => {
    if (!latitude || !longitude || loading || (address !== 'Click to load' && address !== initialAddress)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1Ijoic29waGllMDEiLCJhIjoiY21jbmZtaHBkMDNhMjJuczMxd3dmeWVobCJ9.7W1F9G-KWZRqsiNPEle4sA'}`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        setAddress(data.features[0].place_name);
      } else {
        setAddress('Unknown location');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setAddress(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
    } finally {
      setLoading(false);
    }
  };

  if (!latitude || !longitude) {
    return <Typography variant="body2" color="text.secondary">Unknown</Typography>;
  }

  const isCoordinates = address === 'Click to load' || address === initialAddress;

  return (
    <Tooltip title={isCoordinates ? 'Click to load address' : address}>
      <Box
        onClick={loadAddress}
        sx={{
          cursor: isCoordinates ? 'pointer' : 'default',
          '&:hover': isCoordinates ? { textDecoration: 'underline', color: 'primary.main' } : {}
        }}
      >
        {loading ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={12} />
            <Typography variant="body2">Loading...</Typography>
          </Box>
        ) : (
          <Typography
            variant="body2"
            sx={{
              maxWidth: 200,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: isCoordinates ? 'primary.main' : 'text.primary'
            }}
          >
            {address}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};



const DevicesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [devices, setDevices] = useState<DeviceWithTelemetry[]>([]);
  const [filteredDevices, setFilteredDevices] = useState<DeviceWithTelemetry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [deviceType, setDeviceType] = useState<string>('');
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openBulkImport, setOpenBulkImport] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openQuickVehicle, setOpenQuickVehicle] = useState(false);
  const [selectedDeviceForVehicle, setSelectedDeviceForVehicle] = useState<DeviceWithTelemetry | null>(null);



  // Reverse geocode coordinates to address
  const reverseGeocode = async (lat: number, lng: number): Promise<string> => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1Ijoic29waGllMDEiLCJhIjoiY21jbmZtaHBkMDNhMjJuczMxd3dmeWVobCJ9.7W1F9G-KWZRqsiNPEle4sA'}`
      );
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        return data.features[0].place_name;
      }
      return 'Unknown location';
    } catch (error) {
      console.error('Geocoding error:', error);
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  };
  const enrichDevicesWithTelemetry = async (
    devicesList: Device[]
  ): Promise<DeviceWithTelemetry[]> => {
    
    if (devicesList.length === 0) return [];
  
    // One single API call for all devices
    const imeis = devicesList.map(d => d.imei).join(',');
    
    let telemetryMap: Record<string, any> = {};
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:1001/api'}/telemetry/latest-bulk?imeis=${imeis}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      if (response.ok) {
        const result = await response.json();
        telemetryMap = result.data || {};
      }
    } catch (err) {
      console.error('Failed to fetch bulk telemetry:', err);
    }
  
    // Now map each device to its telemetry — no more async calls
    return devicesList.map(device => {
      const telemetry = telemetryMap[device.imei];
  
      if (!telemetry) {
        // No telemetry found — return device with defaults
        const fallbackLat = device.location?.coordinates?.[1];
        const fallbackLng = device.location?.coordinates?.[0];
        return {
          ...device,
          lastSpeed: 0,
          latitude: fallbackLat,
          longitude: fallbackLng,
          address: fallbackLat && fallbackLng
            ? `${fallbackLat.toFixed(4)}, ${fallbackLng.toFixed(4)}`
            : 'Unknown',
          engineStatus: false,
        };
      }
  
      const reported = telemetry.state?.reported || {};
      const isJTT808 = telemetry.manufacturer === 'JTT808' 
        || telemetry.protocol === 'JTT808';
  
      // Speed
      const speed = isJTT808
        ? (telemetry.speed_kmh ?? 0)
        : (reported.sp ?? 0);
  
      // Location
      let latitude: number | undefined;
      let longitude: number | undefined;
  
      if (telemetry.parsed_location) {
        latitude = telemetry.parsed_location.latitude;
        longitude = telemetry.parsed_location.longitude;
      }else if (telemetry.parsed_location?.latitude && telemetry.parsed_location?.longitude) {
        latitude = telemetry.parsed_location.latitude;
        longitude = telemetry.parsed_location.longitude;
      } else if (reported.latlng) {
        const coords = reported.latlng.split(',')
          .map((c: string) => parseFloat(c.trim()));
        latitude = coords[0];
        longitude = coords[1];
      } else if (telemetry.latitude && telemetry.longitude) {
        latitude = telemetry.latitude;
        longitude = telemetry.longitude;
      }
  
      // Engine/ignition
      const engineStatus = isJTT808
        ? (telemetry.acc_on === true)
        : (reported['239'] === 1);
  
      // Battery voltage — field 67 (mV → V)
      const batteryVoltage = (() => {
        if (reported['67'] != null)
          return (reported['67'] / 1000).toFixed(2);
        if (typeof telemetry.battery_voltage_V === 'number')
          return telemetry.battery_voltage_V.toFixed(2);
        return null;
      })();
  
      // External voltage — field 66 (mV → V)
      const externalVoltage = (() => {
        if (reported['66'] != null)
          return (reported['66'] / 1000).toFixed(2);
        return null;
      })();
  
      // Odometer
      const totalDistance = reported['16']
        ? Math.round(reported['16'] / 1000)
        : (telemetry.total_mileage_km ?? 0);
  
      // GSM signal
      const gsmSignal = reported['21']
        ?? telemetry.wireless_signal_strength
        ?? 0;
  
      // Satellites
      const satellites = reported.sat
        ?? telemetry.gnss_satellites
        ?? 0;
  
      const heading = reported.ang ?? telemetry.direction ?? 0;
      const altitude = reported.alt ?? telemetry.altitude ?? 0;
  
      return {
        ...device,
        lastSpeed: Math.round(speed),
        latitude,
        longitude,
        address: latitude && longitude
          ? `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`
          : 'Unknown',
        engineStatus,
        batteryVoltage,
        externalVoltage,
        totalDistance,
        gsmSignal,
        satellites,
        heading,
        altitude,
        lastUpdate: telemetry.processed_at,
      };
    });
  };
  const loadDevices = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await deviceAPI.getAllDevices({ page: 1, limit: 100 });

      // ✅ LOG THE RAW API RESPONSE
      console.log('📦 Raw API Response:', data);
      data.forEach((d: any) => {
        console.log(`IMEI: ${d.imei}, VIN: ${d.vin}, Vehicle VIN: ${d.vehicleId?.vin}`);
        console.log(`  Device modelYear: ${d.modelYear}`); // 👈 ADD THIS
        console.log(`  Vehicle year: ${d.vehicleId?.year}`); // 👈 ADD THIS
        console.log(`  vehicleId type: ${typeof d.vehicleId}`); // 👈 ADD THIS
        console.log(`  vehicleId data:`, d.vehicleId); // 👈 ADD THIS
      });


      const enrichedData = await enrichDevicesWithTelemetry(data);
      setDevices(enrichedData);
      setFilteredDevices(enrichedData);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let filtered = [...devices];

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(d => {
        // Extract vehicle data safely
        const vehicleData = typeof d.vehicleId === 'object' && d.vehicleId ? d.vehicleId : null;

        return (
          // Device fields
          d.imei?.toLowerCase().includes(searchLower) ||
          d.plateNumber?.toLowerCase().includes(searchLower) ||
          d.make?.toLowerCase().includes(searchLower) ||
          d.model?.toLowerCase().includes(searchLower) ||
          d.modelYear?.toLowerCase().includes(searchLower) ||
          d.vin?.toLowerCase().includes(searchLower) ||
          d.address?.toLowerCase().includes(searchLower) ||

          // Vehicle fields (from populated vehicleId)
          vehicleData?.plateNumber?.toLowerCase().includes(searchLower) ||
          vehicleData?.make?.toLowerCase().includes(searchLower) ||
          vehicleData?.model?.toLowerCase().includes(searchLower) ||
          vehicleData?.year?.toString().toLowerCase().includes(searchLower) ||
          vehicleData?.vin?.toLowerCase().includes(searchLower)
        );
      });
    }

    if (status) {
      filtered = filtered.filter(d => {
        const realtimeStatus = getDeviceRealTimeStatus(d);
        return realtimeStatus.status === status;
      });
    }

    if (deviceType) {
      filtered = filtered.filter(d => d.deviceType === deviceType);
    }

    setFilteredDevices(filtered);
  }, [search, status, deviceType, devices]);

  const onlineCount = useMemo(() =>
    devices.filter(d => {
      const rt = getDeviceRealTimeStatus(d);
      return rt.status === 'online';
    }).length,
    [devices]
  );

  const offlineCount = useMemo(() =>
    devices.filter(d => {
      const rt = getDeviceRealTimeStatus(d);
      return rt.status === 'offline';
    }).length,
    [devices]
  );
  const inactiveCount = useMemo(() =>
    devices.filter(d => {
      const rt = getDeviceRealTimeStatus(d);
      return rt.status === 'inactive';
    }).length,
    [devices]
  );

  const handleAdded = async () => {
    await loadDevices();
  };

  const handleBulkImported = async (count: number) => {
    console.log(`${count} devices imported successfully`);
    await loadDevices();
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:1001/api'}/devices/bulk-import/template`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'device_import_template.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading template:', error);
    }
    handleMenuClose();
  };

  const handleQuickAddVehicle = (device: DeviceWithTelemetry) => {
    if (!device.vin) {
      alert('This device does not have a VIN. Cannot create vehicle.');
      return;
    }
    setSelectedDeviceForVehicle(device);
    setOpenQuickVehicle(true);
  };
  // Quick Vehicle Creation Dialog Component
  const QuickVehicleDialog: React.FC<{
    open: boolean;
    onClose: () => void;
    device: DeviceWithTelemetry | null;
  }> = ({ open, onClose, device }) => {
    const [vehicleData, setVehicleData] = useState({
      plateNumber: device?.plateNumber || '',
      category: 'general' as 'executive' | 'staff_bus' | 'technical' | 'general',
      make: device?.make || '',
      model: device?.model || '',
      year: parseInt(device?.modelYear || new Date().getFullYear().toString()),
      color: '',
      fuelType: 'petrol' as 'petrol' | 'diesel' | 'electric' | 'hybrid',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleCreate = async () => {
      if (!device || !device.vin) {
        setError('Device does not have a VIN');
        return;
      }

      if (!vehicleData.plateNumber) {
        setError('Plate number is required');
        return;
      }

      setLoading(true);
      setError('');

      try {
        await vehicleAPI.quickAddFromDevice({
          deviceId: device._id!,
          vin: device.vin,
          plateNumber: vehicleData.plateNumber,
          category: vehicleData.category,
          make: vehicleData.make || device.make,
          model: vehicleData.model || device.model || '',
          year: vehicleData.year,
          color: vehicleData.color,
          fuelType: vehicleData.fuelType,
        });

        alert('✓ Vehicle created and bound successfully!');
        await loadDevices();
        onClose();
      } catch (err: any) {
        console.error('Failed to create vehicle:', err);
        setError(err.response?.data?.message || 'Failed to create vehicle');
      } finally {
        setLoading(false);
      }
    };

    if (!device) return null;

    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create Vehicle from Device
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Device Info Summary */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Device:</strong> {device.imei}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>VIN:</strong> {device.vin}
              </Typography>
              <Typography variant="body2">
                Creating vehicle will automatically bind this device.
              </Typography>
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Vehicle Form */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Plate Number"
                  value={vehicleData.plateNumber}
                  onChange={(e) => setVehicleData(prev => ({
                    ...prev,
                    plateNumber: e.target.value.toUpperCase()
                  }))}
                  error={!vehicleData.plateNumber && error !== ''}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={vehicleData.category}
                    onChange={(e) => setVehicleData(prev => ({
                      ...prev,
                      category: e.target.value as any
                    }))}
                    label="Category"
                  >
                    <MenuItem value="executive">Executive Pool</MenuItem>
                    <MenuItem value="staff_bus">Staff Bus</MenuItem>
                    <MenuItem value="technical">Technical Units</MenuItem>
                    <MenuItem value="general">General Pool</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  label="Make"
                  value={vehicleData.make}
                  onChange={(e) => setVehicleData(prev => ({
                    ...prev,
                    make: e.target.value
                  }))}
                  placeholder={device.make}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  label="Model"
                  value={vehicleData.model}
                  onChange={(e) => setVehicleData(prev => ({
                    ...prev,
                    model: e.target.value
                  }))}
                  placeholder={device.model || ''}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Year"
                  value={vehicleData.year}
                  onChange={(e) => setVehicleData(prev => ({
                    ...prev,
                    year: parseInt(e.target.value)
                  }))}
                  inputProps={{ min: 1900, max: new Date().getFullYear() + 1 }}
                />
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth required>
                  <InputLabel>Fuel Type</InputLabel>
                  <Select
                    value={vehicleData.fuelType}
                    onChange={(e) => setVehicleData(prev => ({
                      ...prev,
                      fuelType: e.target.value as any
                    }))}
                    label="Fuel Type"
                  >
                    <MenuItem value="petrol">Petrol</MenuItem>
                    <MenuItem value="diesel">Diesel</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Color (Optional)"
                  value={vehicleData.color}
                  onChange={(e) => setVehicleData(prev => ({
                    ...prev,
                    color: e.target.value
                  }))}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={loading || !vehicleData.plateNumber}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {loading ? 'Creating...' : 'Create & Bind Vehicle'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };




  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h5" sx={{ mb: 3 }}>Fleet Management</Typography>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                placeholder="Search by IMEI, plate, make or location"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                select
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                label="Type"
                value={deviceType}
                onChange={(e) => setDeviceType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="My Car Pro">My Car Pro</MenuItem>
                <MenuItem value="My Car Lite">My Car Lite</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4} display="flex" flexDirection={{ xs: 'row', md: 'row' }} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} gap={1} flexWrap="wrap">
              <Button
                variant="outlined"
                startIcon={!isMobile && <RefreshIcon />}
                onClick={loadDevices}
                size={isMobile ? 'small' : 'medium'}
              >
                {isMobile ? 'Refresh' : 'Refresh'}
              </Button>
              <Button
                variant="contained"
                startIcon={!isMobile && <AddIcon />}
                onClick={() => setOpenAdd(true)}
                size={isMobile ? 'small' : 'medium'}
              >
                {isMobile ? 'Add' : 'Add Device'}
              </Button>
              <IconButton onClick={handleMenuClick} size={isMobile ? 'small' : 'medium'}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={() => { setOpenBulkImport(true); handleMenuClose(); }}>
                  <ListItemIcon>
                    <UploadIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Bulk Import</ListItemText>
                </MenuItem>
                <MenuItem onClick={downloadTemplate}>
                  <ListItemIcon>
                    <DownloadIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Download Template</ListItemText>
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
            <Chip label={`All (${devices.length})`} onClick={() => setStatus('')} />
            <Chip
              color="success"
              label={`Online (${onlineCount})`}
              onClick={() => setStatus('online')}
            />
            <Chip
              color="default"
              label={`Offline (${offlineCount})`}
              onClick={() => setStatus('offline')}
            />
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : filteredDevices.length === 0 ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Typography color="text.secondary">
                No devices found. {search || status || deviceType ? 'Try adjusting your filters.' : ''}
              </Typography>
            </Box>
          ) : isMobile ? (
            // Mobile Card View
            <Box>
              {filteredDevices.map((d, idx) => (
                <Card key={d._id || d.imei} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {(typeof d.vehicleId === 'object' && d.vehicleId?.plateNumber) || d.plateNumber || d.make || 'Not Bound to Vehicle'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {(typeof d.vehicleId === 'object' && d.vehicleId ? d.vehicleId.make : d.make) || 'Unknown'} {(typeof d.vehicleId === 'object' && d.vehicleId ? d.vehicleId.model : d.model) || ''} ({(typeof d.vehicleId === 'object' && d.vehicleId ? d.vehicleId.year : d.modelYear) || 'N/A'})
                        </Typography>

                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          IMEI: {d.imei}
                        </Typography>
                      </Box>
                      {(() => {
                        const realtimeStatus = getDeviceRealTimeStatus(d);
                        return <Chip size="small" color={realtimeStatus.color} label={realtimeStatus.label} />;
                      })()}
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Speed</Typography>
                        <Typography
                          variant="body2"
                          color={d.lastSpeed && d.lastSpeed > 0 ? 'success.main' : 'text.secondary'}
                          fontWeight={d.lastSpeed && d.lastSpeed > 0 ? 'bold' : 'normal'}
                        >
                          {d.lastSpeed || 0} km/h
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Engine</Typography>
                        <Box>
                          <Chip
                            size="small"
                            label={d.engineStatus &&
                              getDeviceRealTimeStatus(d).status !== 'offline'
                              ? 'ON' : 'OFF'}
                            color={d.engineStatus &&
                              getDeviceRealTimeStatus(d).status !== 'offline'
                              ? 'success' : 'default'}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          {d.engineStatus && d.lastSpeed === 0 && d.idleTime && d.idleTime > 0 && (
                            <Typography variant="caption" color="warning.main" display="block">
                              Idle {d.idleTime}m
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Signal/Sats</Typography>
                        <Typography variant="body2">
                          {renderSignalBars(d.gsmSignal || 0)} • {d.satellites || 0} sats
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Battery</Typography>
                        <Typography variant="body2">
                          {d.batteryVoltage ? `${d.batteryVoltage}V` : '-'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Location</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <LocationCell
                            latitude={d.latitude}
                            longitude={d.longitude}
                            initialAddress={d.address}
                          />
                        </Box>
                      </Grid>

                      {d.latitude && d.longitude && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">Coordinates</Typography>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', display: 'block' }}>
                            {d.latitude.toFixed(6)}, {d.longitude.toFixed(6)}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                    {/* VIN Status in Mobile View */}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">VIN Status</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {d.vin ? (
                          <>
                            <Chip
                              size="small"
                              icon={<CheckCircle />}
                              label={`VIN: ${d.vin}`}
                              color="success"
                              sx={{ mb: 1, fontSize: '0.7rem' }}
                            />
                            {!d.isBoundToVehicle && (
                              <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() => handleQuickAddVehicle(d)}
                              >
                                Create Vehicle
                              </Button>
                            )}
                            {d.isBoundToVehicle && (
                              <Chip
                                size="small"
                                icon={<LinkIcon />}
                                label="Vehicle Bound"
                                color="success"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </>
                        ) : (
                          <Chip
                            size="small"
                            icon={<Warning />}
                            label="No VIN Available"
                            color="warning"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>S/N</TableCell>
                    <TableCell>Device Name</TableCell>
                    <TableCell>IMEI</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Speed</TableCell>
                    <TableCell>Engine</TableCell>
                    <TableCell>Signal</TableCell>
                    <TableCell>Battery</TableCell>
                    <TableCell>Odometer</TableCell>
                    <TableCell>Coordinates</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDevices.map((d, idx) => (
                    <TableRow key={d._id || d.imei} hover>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {(typeof d.vehicleId === 'object' && d.vehicleId?.plateNumber) || d.plateNumber || d.make || 'Not Bound to Vehicle'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(typeof d.vehicleId === 'object' && d.vehicleId ? d.vehicleId.make : d.make) || 'Unknown'} {(typeof d.vehicleId === 'object' && d.vehicleId ? d.vehicleId.model : d.model) || ''} ({(typeof d.vehicleId === 'object' && d.vehicleId ? d.vehicleId.year : d.modelYear) || 'N/A'})
                          </Typography>
                          {/* VIN Status Indicator */}
                          <Box sx={{ mt: 0.5 }}>
                            {d.vin ? (
                              <Chip
                                size="small"
                                icon={<CheckCircle />}
                                label={`VIN: ${d.vin.substring(0, 8)}...`}
                                color="success"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            ) : (
                              <Chip
                                size="small"
                                icon={<Warning />}
                                label="No VIN"
                                color="warning"
                                sx={{ fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      </TableCell>


                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {d.imei}
                        </Typography>
                      </TableCell>

                      {/* NEW: Type Column */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={d.deviceType || 'Unknown'}
                          color={d.deviceType === 'My Car Pro' ? 'primary' : 'default'}
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>

                      <TableCell>
                        {(() => {
                          const realtimeStatus = getDeviceRealTimeStatus(d);
                          return <Chip size="small" color={realtimeStatus.color} label={realtimeStatus.label} />;
                        })()}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography
                            variant="body2"
                            color={d.lastSpeed && d.lastSpeed > 0 &&
                              getDeviceRealTimeStatus(d).status !== 'offline'
                              ? 'success.main' : 'text.secondary'}
                          >
                            {getDeviceRealTimeStatus(d).status === 'offline'
                              ? 0 : (d.lastSpeed || 0)} km/h
                          </Typography>
                          {d.heading !== undefined && (
                            <Typography variant="caption" color="text.secondary">
                              {d.heading}° {getCardinalDirection(d.heading)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Chip
                            size="small"
                            label={d.engineStatus &&
                              getDeviceRealTimeStatus(d).status !== 'offline'
                              ? 'ON' : 'OFF'}
                            color={d.engineStatus &&
                              getDeviceRealTimeStatus(d).status !== 'offline'
                              ? 'success' : 'default'}
                            sx={{ minWidth: 50 }}
                          />
                          {d.engineStatus && d.lastSpeed === 0 && d.idleTime && d.idleTime > 0 && (
                            <Typography variant="caption" color="warning.main" display="block">
                              Idle {d.idleTime}m
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2">
                            {renderSignalBars(d.gsmSignal || 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {d.satellites || 0} sats
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {d.batteryVoltage ? `${d.batteryVoltage}V` : '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Ext: {d.externalVoltage ? `${d.externalVoltage}V` : '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {d.totalDistance ? `${d.totalDistance.toLocaleString()} km` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {d.latitude && d.longitude ? (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {d.latitude.toFixed(6)}, {d.longitude.toFixed(6)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <LocationCell
                          latitude={d.latitude}
                          longitude={d.longitude}
                          initialAddress={d.address}
                        />
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {/* Show quick add vehicle button if device has VIN but no vehicle bound */}
                          {d.vin && !d.isBoundToVehicle && (
                            <Tooltip title="Create Vehicle from Device">
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => handleQuickAddVehicle(d)}
                                sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                              >
                                Add Vehicle
                              </Button>
                            </Tooltip>
                          )}

                          {/* Show if device is bound */}
                          {d.isBoundToVehicle && (
                            <Chip
                              size="small"
                              icon={<LinkIcon />}
                              label="Vehicle Bound"
                              color="success"
                              sx={{ fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      <AddDeviceDialog open={openAdd} onClose={() => setOpenAdd(false)} onAdded={handleAdded as any} />
      <BulkImportDialog open={openBulkImport} onClose={() => setOpenBulkImport(false)} onImported={handleBulkImported} />

      {/* NEW: Quick Vehicle Creation Dialog */}
      <QuickVehicleDialog
        open={openQuickVehicle}
        onClose={() => {
          setOpenQuickVehicle(false);
          setSelectedDeviceForVehicle(null);
        }}
        device={selectedDeviceForVehicle}
      />
    </Box>
  );
};

export default DevicesPage;